/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { UseInternetFormReturn } from "../hooks/useInternetForm";
import { P2PAddressingTable } from "./P2PAddressingTable";
import { BgpNeighborsCard } from "./BgpNeighborsCard";
import { SecondaryVlanInput } from "./SecondaryVlanInput";
import {
  p2pAddressingError as describeP2PAddressingError,
  secondaryVlanError,
} from "../internetAddressing";
import { LatencyTargetsTable } from "./LatencyTargetsTable";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";

export interface InternetCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  // Port dropdown
  portOptions: Array<{ id: number; label: string }>;
  selectedPortId: number | null;
  getPortLabelById: (id: number | null) => string;
  handlePortSelect: (label: string) => void;
  // Form state management
  internetForm: UseInternetFormReturn;
  // Validation and loading states
  isFormValid?: boolean;
  isCreating?: boolean;
}

export const InternetCreateModal: React.FC<InternetCreateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  portOptions,
  selectedPortId,
  getPortLabelById,
  handlePortSelect,
  internetForm,
  isFormValid = false,
  isCreating = false,
}) => {
  const { formData, updateField } = internetForm;

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("internet-information");
  
  // Local state for tags input to preserve raw input (including trailing commas)
  const [tagsInput, setTagsInput] = useState<string>("");
  
  // Error state for P2P addressing validation
  const [p2pAddressingError, setP2PAddressingError] = useState<string>("");
  
  // Error states for required fields in step 1
  const [fieldErrors, setFieldErrors] = useState<{
    kontrolEdgePortId?: string;
    subscriptionId?: string;
    name?: string;
    mbps?: string;
    vlan?: string;
    countryCode?: string;
    automaticVlan?: string;
    redundancy?: string;
    autoRenew?: string;
  }>({});

  // Initialize tagInput when modal opens, reset when it closes
  useEffect(() => {
    if (isOpen) {
      if (formData.tag && formData.tag.length > 0) {
        setTagsInput(formData.tag.join(", "));
      } else {
        setTagsInput("");
      }
    } else {
      setTagsInput("");
      setP2PAddressingError(""); // Clear error when modal closes
      setFieldErrors({}); // Clear field errors when modal closes
    }
  }, [isOpen]); // Only sync when modal opens/closes, not when formData.tag changes

  // Clear error when switching tabs manually
  useEffect(() => {
    if (activeTab !== "p2p-addressing") {
      setP2PAddressingError("");
    }
  }, [activeTab]);

  const tabs = [
    { label: "Internet Information", value: "internet-information" },
    { label: "P2P Addressing", value: "p2p-addressing" },
  ];

  const handleStatusToggle = (isActive: boolean) => {
    // Switch: false = Draft (0), true = Active (1)
    updateField("isDraft", isActive ? 1 : 0);
  };
  const isActive = formData.isDraft === 1;

  const statusLabel = formData.isDraft === 0 ? "Draft" : "Active";
  const statusVariant = formData.isDraft === 0 ? "neutral" : "success";

  // Determine if location dropdowns should be disabled (disabled on Tab 2)
  const isLocationDisabled = activeTab === "p2p-addressing";

  // Validate required fields for tab 1 (Internet Information)
  // Tags are optional, everything else is required
  const isTab1Valid = useMemo(() => {
    return (
      formData.kontrolEdgePortId !== null &&
      formData.mbps !== null &&
      formData.vlan !== null &&
      formData.automaticVlan !== null &&
      formData.redundancy !== null &&
      formData.countryCode.trim() !== "" &&
      formData.autoRenew !== null &&
      formData.subscriptionId.trim() !== "" &&
      formData.name.trim() !== ""
    );
  }, [formData]);

  // The primary P2P leg is required on create; a secondary is all or nothing.
  const p2pValidationError = useMemo(
    () => describeP2PAddressingError(formData.p2pAddressing, { requirePrimary: true }),
    [formData.p2pAddressing]
  );
  const vlanSecondaryError = secondaryVlanError(
    formData.vlan,
    formData.p2pAddressing.vlanSecondary
  );

  // Validate step 1 fields and set errors
  const validateStep1 = (): boolean => {
    const errors: typeof fieldErrors = {};
    
    if (selectedPortId === null || formData.kontrolEdgePortId === null) {
      errors.kontrolEdgePortId = "Kontrol Edge Port is required";
    }
    if (formData.subscriptionId.trim() === "") {
      errors.subscriptionId = "Subscription ID is required";
    }
    if (formData.name.trim() === "") {
      errors.name = "Name is required";
    }
    if (formData.mbps === null) {
      errors.mbps = "Mbps is required";
    }
    if (formData.vlan === null) {
      errors.vlan = "VLAN is required";
    }
    if (formData.countryCode.trim() === "") {
      errors.countryCode = "Country Code is required";
    }
    if (formData.automaticVlan === null) {
      errors.automaticVlan = "Automatic VLAN is required";
    }
    if (formData.redundancy === null) {
      errors.redundancy = "Redundancy is required";
    }
    if (formData.autoRenew === null) {
      errors.autoRenew = "Auto Renew is required";
    }

    setFieldErrors(errors);
    // The secondary VLAN shows its own error inline, it is not a required field.
    return Object.keys(errors).length === 0 && vlanSecondaryError === null;
  };

  // Handle tab change - validate step 1 if trying to go to step 2
  const handleTabChange = (newTab: string) => {
    if (newTab === "p2p-addressing" && activeTab === "internet-information") {
      if (!validateStep1()) {
        return;
      }
    }
    setActiveTab(newTab);
    // Clear P2P error when switching away from P2P tab
    if (newTab !== "p2p-addressing") {
      setP2PAddressingError("");
    }
  };

  // Handle Next button click - validate before proceeding to tab 2
  const handleNext = () => {
    if (validateStep1()) {
      setActiveTab("p2p-addressing");
      setP2PAddressingError("");
    }
  };

  // Handle Back button click - return to Internet Information tab
  const handleBack = () => {
    setActiveTab("internet-information");
    // Clear P2P addressing error when going back
    setP2PAddressingError("");
  };
  
  // Handle Create button click - validate P2P addressing before creating
  const handleCreate = () => {
    // Clear previous error
    setP2PAddressingError("");
    
    // Validate P2P addressing
    if (p2pValidationError) {
      setP2PAddressingError(p2pValidationError);
      setActiveTab("p2p-addressing");
      return;
    }
    onSave();
  };
  
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Internet Service"
      customWidth="max-w-6xl"
    >
    <div className="flex flex-col max-h-[80vh] h-[900px] overflow-hidden">  
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6">
            {/* Tabs Navigation */}
            <GenericTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />

        {/* Tab Content */}
        {activeTab === "internet-information" && (
          <>
            {/* Port Selection - Always visible */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Location Selection
              </h3>
              <div className="flex flex-col space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">
                    Kontrol Edge Port <span className="text-red-500">*</span>
                  </label>
                  <GenericDropdown
                    items={portOptions.map((p) => p.label)}
                    value={getPortLabelById(selectedPortId)}
                    onSelect={(label) => {
                      handlePortSelect(label);
                      // Clear error when port is selected
                      if (fieldErrors.kontrolEdgePortId) {
                        setFieldErrors(prev => ({ ...prev, kontrolEdgePortId: undefined }));
                      }
                    }}
                    disabled={isLocationDisabled}
                    defaultLabel="Select Port"
                    minWidthClass="min-w-[300px]"
                    maxWidthClass="max-w-[400px]"
                    dropdownMaxHeightClass="max-h-48"
                    fixedPosition={true}
                  />
                  {fieldErrors.kontrolEdgePortId && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.kontrolEdgePortId}</p>
                  )}
                </div>
              </div>
            </GenericCard>
            {/* Internet Information Section */}
            <GenericCard isCollapsible={false} className="p-5">
                {/* Status Toggle Switch */}
                <div className="flex items-center justify-end gap-2 mb-4">
                  <label className="text-sm font-medium text-semantic-text-secondary flex items-center gap-1">
                    Status:
                    <GenericTooltip
                      text="You can toggle the status of this configuration by using the switch."
                      position="top"
                    />
                  </label>
                  <span
                    className={`text-sm ${
                      !isActive
                        ? "font-semibold text-semantic-text-primary"
                        : "text-semantic-text-secondary"
                    }`}
                  >
                    Draft
                  </span>
                  <GenericSwitch
                    checked={isActive}
                    onChange={handleStatusToggle}
                  />
                  <span
                    className={`text-sm ${
                      isActive
                        ? "font-semibold text-semantic-text-primary"
                        : "text-semantic-text-secondary"
                    }`}
                  >
                    Active
                  </span>
                </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <GenericInput 
                    label={
                      <>
                        Subscription ID <span className="text-red-500">*</span>
                      </>
                    }
                    value={formData.subscriptionId} 
                    onChange={(value) => {
                      updateField("subscriptionId", value);
                      // Clear error when field is updated
                      if (fieldErrors.subscriptionId) {
                        setFieldErrors(prev => ({ ...prev, subscriptionId: undefined }));
                      }
                    }} 
                  />
                  {fieldErrors.subscriptionId && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.subscriptionId}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <GenericInput 
                    label={
                      <>
                        Name <span className="text-red-500">*</span>
                      </>
                    }
                    value={formData.name} 
                    onChange={(value) => {
                      updateField("name", value);
                      // Clear error when field is updated
                      if (fieldErrors.name) {
                        setFieldErrors(prev => ({ ...prev, name: undefined }));
                      }
                    }} 
                  />
                  {fieldErrors.name && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.name}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <GenericInput
                    label={
                      <>
                        Mbps <span className="text-red-500">*</span>
                      </>
                    }
                    value={formData.mbps ? String(formData.mbps) : ""}
                    onChange={(value) => {
                      updateField("mbps", value ? Number(value) : null);
                      // Clear error when field is updated
                      if (fieldErrors.mbps) {
                        setFieldErrors(prev => ({ ...prev, mbps: undefined }));
                      }
                    }}
                    type="number"
                  />
                  {fieldErrors.mbps && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.mbps}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <GenericInput
                    label={
                      <>
                        VLAN <span className="text-red-500">*</span>
                      </>
                    }
                    value={formData.vlan ? String(formData.vlan) : ""}
                    onChange={(value) => {
                      updateField("vlan", value ? Number(value) : null);
                      // Clear error when field is updated
                      if (fieldErrors.vlan) {
                        setFieldErrors(prev => ({ ...prev, vlan: undefined }));
                      }
                    }}
                    type="number"
                  />
                  {fieldErrors.vlan && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.vlan}</p>
                  )}
                </div>
                <SecondaryVlanInput
                  p2pAddressing={formData.p2pAddressing}
                  error={vlanSecondaryError}
                  onUpdate={(p2p) => updateField("p2pAddressing", p2p)}
                />
                <div className="flex flex-col">
                  <GenericInput
                    label={
                      <>
                        Country Code <span className="text-red-500">*</span>
                      </>
                    }
                    value={formData.countryCode}
                    placeholder="MX"
                    onChange={(value) => {
                      updateField("countryCode", value);
                      // Clear error when field is updated
                      if (fieldErrors.countryCode) {
                        setFieldErrors(prev => ({ ...prev, countryCode: undefined }));
                      }
                    }}
                  />
                  {fieldErrors.countryCode && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors.countryCode}</p>
                  )}
                </div>
                <GenericInput 
                  label="Tags" 
                  value={tagsInput} 
                  onChange={(value) => {
                    // Update local state to preserve raw input (including trailing commas)
                    setTagsInput(value);
                    // Convert to array for parent state (for change detection), but keep the raw string in local state
                    // This allows the user to type commas without them being removed
                    const tags = value.split(",").map(t => t.trim()).filter(Boolean);
                    updateField("tag", tags);
                  }} 
                  placeholder="Enter tags separated by commas"
                />
                <div className="col-span-2 grid grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Automatic VLAN <span className="text-red-500">*</span>
                    </label>
                    <GenericDropdown
                      items={["Yes", "No"]}
                      value={formData.automaticVlan === null ? "" : (formData.automaticVlan ? "Yes" : "No")}
                      defaultLabel="Select Automatic VLAN"
                      onSelect={(val) => {
                        updateField("automaticVlan", val === "Yes");
                        // Clear error when field is updated
                        if (fieldErrors.automaticVlan) {
                          setFieldErrors(prev => ({ ...prev, automaticVlan: undefined }));
                        }
                      }}
                    />
                    {fieldErrors.automaticVlan && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.automaticVlan}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Redundancy <span className="text-red-500">*</span>
                    </label>
                    <GenericDropdown
                      items={["Yes", "No"]}
                      value={formData.redundancy === null ? "" : (formData.redundancy ? "Yes" : "No")}
                      defaultLabel="Select Redundancy"
                      onSelect={(val) => {
                        updateField("redundancy", val === "Yes");
                        // Clear error when field is updated
                        if (fieldErrors.redundancy) {
                          setFieldErrors(prev => ({ ...prev, redundancy: undefined }));
                        }
                      }}
                    />
                    {fieldErrors.redundancy && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.redundancy}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Auto Renew <span className="text-red-500">*</span>
                    </label>
                    <GenericDropdown
                      items={["Yes", "No"]}
                      value={formData.autoRenew === null ? "" : (formData.autoRenew ? "Yes" : "No")}
                      defaultLabel="Select Auto Renew"
                      onSelect={(val) => {
                        updateField("autoRenew", val === "Yes");
                        // Clear error when field is updated
                        if (fieldErrors.autoRenew) {
                          setFieldErrors(prev => ({ ...prev, autoRenew: undefined }));
                        }
                      }}
                    />
                    {fieldErrors.autoRenew && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.autoRenew}</p>
                    )}
                  </div>
                  </div>
              </div>
            </GenericCard>
          </>
        )}

        {activeTab === "p2p-addressing" && (
          <>
            {/* Error message for P2P addressing validation */}
            {p2pAddressingError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 font-medium">{p2pAddressingError}</p>
              </div>
            )}
            <P2PAddressingTable
              p2pAddressing={formData.p2pAddressing}
              onUpdate={(p2pData) => {
                updateField("p2pAddressing", p2pData);
                // Clear error when user updates P2P addressing
                if (p2pAddressingError) {
                  setP2PAddressingError("");
                }
              }}
            />
            <BgpNeighborsCard
              bgpConfig={formData.bgpConfig}
              onUpdate={(bgpConfig) => updateField("bgpConfig", bgpConfig)}
            />

            <LatencyTargetsTable
              latencyTargets={formData.latencyTargets}
              onUpdate={(latencyData) => {
                updateField("latencyTargets", latencyData);
              }}
            />
          </>
        )}
      </div>
      </div>

        {/* Fixed buttons at bottom */}
        <div className="flex justify-end gap-2 px-1 mt-6 pt-4 flex-shrink-0">
          {activeTab === "internet-information" ? (
            <>
              <GenericButton 
                label="Cancel" 
                variant="secondary" 
                onClick={onClose}
                disabled={isCreating}
              />
              <GenericButton 
                label="Next" 
                variant="brand" 
                onClick={handleNext}
                disabled={isCreating}
              />
            </>
          ) : (
            <>
              <GenericButton 
                label="Back" 
                variant="secondary" 
                onClick={handleBack}
                disabled={isCreating}
              />
              <GenericButton 
                label="Create" 
                variant="brand" 
                onClick={handleCreate}
                disabled={isCreating}
                loading={isCreating} 
              />
            </>
          )}
        </div>
      </div>
    </GenericModal>
  );
};

